<template>
    <div>
        <pc-loader
            v-bind:loading="loading"
            v-bind:message="generatingDocumentLoadingText">
            <envelope-item-definition-actions
                v-if="!showUploader"
                v-bind:item-definition="itemDefinition" />
            <div v-if="showUploader">
                <div class="d-flex align-items-center">
                    <pc-button
                        v-if="showGenerateBtn"
                        v-bind:disabled="readonly"
                        class="me-2"
                        small
                        v-on:click="onGenerateItem">
                        {{ generateButton }}
                    </pc-button>
                    <pc-file
                        v-model="itemDocument"
                        v-bind:is-temporary="false"
                        v-bind:extensions="extensions"
                        v-bind:readonly="readonly"
                        v-bind:upload-function="uploadItemDocument"
                        v-bind:view-function="downloadWetSignItemDocument"
                        v-bind:show-remove="discardable"
                        v-bind:alt-label="uploadWetSignDocumentAltText"
                        required
                        class="flex-grow-1"
                        v-on:uploading-changed="uploadingChanged($event)"
                        v-on:upload-canceled="uploadCanceled" />
                </div>
                <div class="small">
                    {{ uploadedBy }}
                </div>
            </div>
        </pc-loader>
    </div>
</template>
<script>
    import Vuex from "vuex";
    import { PROJECT_FORM_TYPES } from "Forms/Components/ProjectForm/projectFormStore";
    import { PROJECT_FORM_CONTROLLER_TYPES } from "Forms/Components/ProjectForm/projectFormControllerStore";
    import { ENVELOPE_ITEM_TYPE } from "ESignatures/EditEnvelope/editEnvelopeStore";
    import envelopeItemDefinitionActions from "./envelopeItemDefinitionActions.vue";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "../i18n/eSignature.i18n";

    import { formatBasicUserInfoForDisplay } from "Util/clientUser";

    export default {
        components: {
            envelopeItemDefinitionActions
        },
        inject: {
            readonly: {
                from: "readonly",
                default: false
            },
            isProjectFormControllerModuleRegistered: {
                from: "isProjectFormControllerModuleRegistered",
                default: false
            }
        },
        props: {
            envelopeId: { type: String, required: true },
            item: { type: Object, required: true },
            itemDefinition: { type: Object, required: true },
            discardable: { type: Boolean, required: false, default: true }
        },
        data() {
            return {
                loading: false,
                uploading: false,

                extensions: ["pdf"]
            };
        },
        computed: {
            ...mapI18n(KEYS, NAMESPACE),
            itemType() {
                return this.itemDefinition.Type;
            },
            itemId() {
                return this.item.PublicId;
            },
            itemDocument: {
                get() {
                    return this.item.Document;
                },
                set(value) {
                    /**
                     * we only need to listen to clear events, no need to
                     * bother with actually setting otherwise since we are overriding
                     * the default upload method of the pc-file component,
                     * and that method will set the value in the store
                     */
                    if (!value && this.itemDocument) {
                        this.removeItemDocument();
                    }
                }
            },
            showUploader() {
                return this.itemType === ENVELOPE_ITEM_TYPE.DocumentTemplate;
            },
            showGenerateBtn() {
                return !this.itemDocument;
            },
            uploadedBy() {
                if (!this.itemDocument) {
                    return null;
                }

                const uploadUserName = this.itemDocument.UploadUser
                    ? formatBasicUserInfoForDisplay(this.itemDocument.UploadUser)
                    : this.wetSignUploadedByUnknownUser;

                return this.$app.res({
                    namespace: NAMESPACE,
                    key: KEYS.wetSignUploadedByText,
                    values: {
                        username: uploadUserName,
                        uploadTimestamp: this.itemDocument.UploadTimestamp.Display
                    }
                });
            }
        },
        methods: {
            uploadingChanged(value) {
                this.uploading = value;
            },
            uploadCanceled() {
                if (this.xhr) {
                    this.uploading = false;
                    this.xhr.abort();
                }
            },
            uploadItemDocument(file) {
                if (this.isProjectFormControllerModuleRegistered) {
                    return this.uploadWetSignEnvelopeItem({
                        file,
                        envelopeId: this.envelopeId,
                        envelopeItemId: this.itemId,
                        captureXhr: this.captureXhr
                    });
                }
                return null;
            },
            captureXhr(xhr) {
                this.xhr = xhr;
            },
            onGenerateItem() {
                this._generateItemDocument();
            },
            async _generateItemDocument() {
                if (this.loading) {
                    return;
                }

                try {
                    this.loading = true;
                    await this.generateEnvelopeItemDocument({
                        envelopeItemDefinitionId: this.itemDefinition.PublicId
                    });
                } finally {
                    this.loading = false;
                }
            },
            downloadWetSignItemDocument() {
                this.viewWetSignEnvelopeItem({
                    envelopeId: this.envelopeId,
                    envelopeItemId: this.itemId
                });
            },
            async removeItemDocument() {
                if (!this.isProjectFormControllerModuleRegistered) {
                    return;
                }

                try {
                    this.loading = true;
                    await this.removeWetSignEnvelopeItem({
                        envelopeId: this.envelopeId,
                        envelopeItemId: this.itemId
                    });
                } finally {
                    this.loading = false;
                }
            },
            ...Vuex.mapActions({
                generateEnvelopeItemDocument: PROJECT_FORM_TYPES.actions.generateEnvelopeItemDocument,
                uploadWetSignEnvelopeItem: PROJECT_FORM_TYPES.actions.uploadWetSignEnvelopeItem,
                removeWetSignEnvelopeItem: PROJECT_FORM_TYPES.actions.removeWetSignEnvelopeItem,
                viewWetSignEnvelopeItem: PROJECT_FORM_CONTROLLER_TYPES.actions.viewWetSignEnvelopeItem
            })
        }
    };
</script>
